import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Package, Plus, Loader2, X, LayoutGrid } from 'lucide-react'
import { subDays } from 'date-fns'
import { DateRange } from 'react-day-picker'

import { Button } from '@/components/ui/button'
import { CategoryMetricsCard, type CategoryMetrics } from '@/components/competition/CategoryMetricsCard'
import { ProductCompetitiveCard } from '@/components/competition/ProductCompetitiveCard'
import { CompetitorSKUsTable } from '@/components/competition/CompetitorSKUsTable'
import { cn, formatPrice, stripBrandPrefixFromProductName } from '@/lib/utils'
import { useSettings, type TrackedSKUWithCompetitors } from '@/contexts/SettingsContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useTopBar } from '@/contexts/TopBarContext'

import { trpc } from '@/lib/trpc'
import type { TrackedSKUWithCityPrices, CityPriceInfo, CityWithClusters, UserLocationData } from '@/types'
import { MultiSelectCityFilter, type CityClusterSelection } from '@/components/ui/MultiSelectCityFilter'
import { TopBarFilterDropdown } from '@/components/ui/TopBarFilterDropdown'
import { DateRangePicker } from '@/components/ui/DateRangePicker'
import type {
    DummyCompetitorBrandGroup,
    DummyCityAvailability,
} from '@/data/dummy-competitor-data'
import type { Platform } from '@/types'
import type { ProductCompetitivePosition } from '@/types/competition'

// ============================================================================
// GLASS CARD PRIMITIVES (matches Availability module design language)
// ============================================================================

const GLASS_CARD = [
    "rounded-2xl",
    "bg-white/60 dark:bg-white/[0.08]",
    "backdrop-blur-xl backdrop-saturate-[1.4]",
    "border border-white/50 dark:border-white/[0.12]",
    "shadow-[0_2px_16px_rgba(0,0,0,0.06),inset_0_1px_0_0_rgba(255,255,255,0.6)]",
    "dark:shadow-[0_2px_16px_rgba(0,0,0,0.3),inset_0_1px_0_0_rgba(255,255,255,0.08)]",
    "transition-shadow duration-200 ease-out",
].join(' ')

const GLASS_DIVIDER = "h-px bg-gradient-to-r from-transparent via-black/[0.06] dark:via-white/[0.08] to-transparent"

const GLASS_INNER = "rounded-xl bg-white/40 dark:bg-white/[0.06] border border-white/40 dark:border-white/[0.08]"

// ============================================================================
// SKELETON LOADERS (matches Availability module pattern)
// ============================================================================

/** Skeleton matching CategoryMetricsCard layout */
function CategoryMetricsCardSkeleton() {
    return (
        <div className={cn("w-full", GLASS_CARD)}>
            <div className="px-4 pt-4 pb-0 min-h-[120px] flex flex-col justify-between">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 rounded-md bg-muted animate-pulse" />
                        <div className="h-4 w-28 bg-muted animate-pulse rounded" />
                    </div>
                    <div className="ml-8">
                        <div className="h-3 w-24 bg-muted animate-pulse rounded" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 bg-muted animate-pulse rounded" />
                        <div className="space-y-1">
                            <div className="h-3 w-14 bg-muted animate-pulse rounded" />
                            <div className="h-5 w-12 bg-muted animate-pulse rounded" />
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 bg-muted animate-pulse rounded" />
                        <div className="space-y-1">
                            <div className="h-3 w-14 bg-muted animate-pulse rounded" />
                            <div className="h-5 w-12 bg-muted animate-pulse rounded" />
                        </div>
                    </div>
                </div>
            </div>
            <div className="px-4 py-2.5 mt-1 border-t border-black/[0.04] dark:border-white/[0.06]">
                <div className="h-3.5 w-20 bg-muted animate-pulse rounded" />
            </div>
        </div>
    )
}

/** Skeleton matching ProductCompetitiveCard layout */
function ProductCompetitiveCardSkeleton() {
    return (
        <div className={cn("relative overflow-hidden", GLASS_CARD)}>
            <div className="relative z-[1]">
                {/* Header */}
                <div className="p-3 pb-2">
                    <div className="flex gap-3 items-start">
                        <div className="w-11 h-11 rounded-lg bg-muted animate-pulse flex-shrink-0" />
                        <div className="flex-1 min-w-0 space-y-1.5">
                            <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                            <div className="h-3 w-20 bg-muted animate-pulse rounded" />
                        </div>
                        <div className="h-6 w-16 bg-muted animate-pulse rounded-lg flex-shrink-0" />
                    </div>
                </div>

                {/* Divider */}
                <div className={cn("mx-3", GLASS_DIVIDER)} />

                {/* Pricing section */}
                <div className="p-3 pt-2">
                    <div className="flex items-stretch gap-2">
                        <div className={cn("flex-1 p-2 text-center space-y-1.5", GLASS_INNER)}>
                            <div className="h-3 w-8 mx-auto bg-muted animate-pulse rounded" />
                            <div className="h-5 w-12 mx-auto bg-muted animate-pulse rounded" />
                        </div>
                        <div className="flex items-center">
                            <div className="h-3 w-4 bg-muted animate-pulse rounded" />
                        </div>
                        <div className={cn("flex-1 p-2 text-center space-y-1.5", GLASS_INNER)}>
                            <div className="h-3 w-8 mx-auto bg-muted animate-pulse rounded" />
                            <div className="h-5 w-12 mx-auto bg-muted animate-pulse rounded" />
                        </div>
                    </div>
                    {/* Price diff badge placeholder */}
                    <div className="h-7 w-full bg-muted animate-pulse rounded-lg mt-2" />
                </div>

                {/* Footer */}
                <div className="mx-3 mb-2.5 mt-0.5 px-2.5 py-1.5 rounded-xl bg-primary/[0.04] dark:bg-primary/[0.08] border border-primary/10 dark:border-primary/15">
                    <div className="h-3.5 w-24 bg-muted animate-pulse rounded" />
                </div>
            </div>
        </div>
    )
}

// ============================================================================
// CONSTANTS (stable references — never recreated)
// ============================================================================

const PLATFORM_OPTIONS: { value: string; label: string }[] = [
    { value: 'all', label: 'All Platforms' },
    { value: 'blinkit', label: 'Blinkit' },
    { value: 'zepto', label: 'Zepto' },
    { value: 'instamart', label: 'Instamart' },
    { value: 'bbnow', label: 'BBNow' },
] 

// Default date range: last 7 days
const getDefaultDateRange = (): DateRange => ({
    from: subDays(new Date(), 7),
    to: new Date()
})

// ============================================================================
// MAIN COMPONENT (Revamped Layout)
// ============================================================================

export function CompetitorPage() {
    const navigate = useNavigate()

    // Core state
    const { trackedSKUs, isLoading: settingsLoading, userBrand } = useSettings()
    const { currentWorkspace } = useWorkspace()
    const workspaceId = currentWorkspace?.id

    // Filters
    const [platformFilter, setPlatformFilter] = useState<Platform | 'all'>('all')
    const [categoryFilter, setCategoryFilter] = useState<string>('all')
    const [locationSelection, setLocationSelection] = useState<CityClusterSelection>({ cityIds: [], clusterIds: [] })
    const [dateRange, setDateRange] = useState<DateRange | undefined>(getDefaultDateRange)

    // API Data
    const [citiesWithClusters, setCitiesWithClusters] = useState<CityWithClusters[]>([])
    const [citiesLoading, setCitiesLoading] = useState(true)
    const [skusWithCityPrices, setSkusWithCityPrices] = useState<TrackedSKUWithCityPrices[]>([])
    const [cityPricesLoading, setCityPricesLoading] = useState(false)

    // UI states
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
    const [selectedProduct, setSelectedProduct] = useState<ProductCompetitivePosition | null>(null)
    const [showCompetitorsFor, setShowCompetitorsFor] = useState<string | null>(null)

    // Category carousel
    const categoryScrollRef = useRef<HTMLDivElement>(null)

    // Inject filters into the primary TopBar
    const { setFilterContent } = useTopBar()

    const utils = trpc.useUtils()

    // --- DATA FETCHING & PROCESSING (Unchanged logic, optimized for layout) ---

    useEffect(() => {
        async function fetchCitiesWithClusters() {
            try {
                setCitiesLoading(true)
                const result = await utils.store.citiesWithClusters.fetch()
                setCitiesWithClusters(result.cities as CityWithClusters[])
            } catch (error) {
                console.error('Failed to fetch cities with clusters:', error)
            } finally {
                setCitiesLoading(false)
            }
        }
        fetchCitiesWithClusters()
    }, [])

    useEffect(() => {
        async function fetchCityPrices() {
            if (!workspaceId) return
            try {
                setCityPricesLoading(true)
                let allSelectedCityIds: number[] = []
                if (locationSelection.cityIds.length > 0) {
                    for (const selectedCityId of locationSelection.cityIds) {
                        const city = citiesWithClusters.find(c => c.id === selectedCityId)
                        if (city && city.allCityIds) {
                            allSelectedCityIds.push(...city.allCityIds)
                        } else {
                            allSelectedCityIds.push(selectedCityId)
                        }
                    }
                }
                const platform = platformFilter !== 'all' ? platformFilter : undefined
                const cityIdsParam = allSelectedCityIds.length > 0 ? allSelectedCityIds : undefined
                // Invalidate cache to ensure fresh data
                await utils.trackedSKU.listWithCityPrices.invalidate()
                const result = await utils.trackedSKU.listWithCityPrices.fetch({ 
                    workspaceId, 
                    cityIds: cityIdsParam, 
                    platform
                })
                setSkusWithCityPrices(result.skus as unknown as TrackedSKUWithCityPrices[])
            } catch (error) {
                console.error('Failed to fetch city prices:', error)
            } finally {
                setCityPricesLoading(false)
            }
        }
        fetchCityPrices()
    }, [workspaceId, locationSelection, platformFilter, citiesWithClusters])

    const categoryMetrics = useMemo<CategoryMetrics[]>(() => {
        const hasLocationFilter = locationSelection.cityIds.length > 0 || locationSelection.clusterIds.length > 0
        const categoryMap = new Map<string, TrackedSKUWithCompetitors[]>()

        trackedSKUs.forEach(sku => {
            const category = sku.category?.trim() || 'Uncategorized'
            const existing = categoryMap.get(category) || []
            categoryMap.set(category, [...existing, sku])
        })

        return Array.from(categoryMap.entries()).map(([categoryName, skus]) => {
            const totalCompetitors = skus.reduce((sum, sku) => sum + (sku.competitors?.length ?? 0), 0)
            let avgSellingPrice = 0
            let avgDiscountPercent = 0

            if (hasLocationFilter && skusWithCityPrices.length > 0) {
                const categorySkuIds = new Set(skus.map(s => s.id))
                const categoryCitySkus = skusWithCityPrices.filter(s => categorySkuIds.has(s.id))
                const allCityPrices: number[] = []
                const allCityDiscounts: number[] = []

                categoryCitySkus.forEach(sku => {
                    sku.competitors?.forEach(comp => {
                        comp.cityPrices?.forEach(cp => {
                            if (cp.price != null && cp.price > 0) allCityPrices.push(cp.price)
                            if (cp.discountPercent != null) allCityDiscounts.push(cp.discountPercent)
                        })
                    })
                })
                avgSellingPrice = allCityPrices.length > 0 ? allCityPrices.reduce((a, b) => a + b, 0) / allCityPrices.length : 0
                avgDiscountPercent = allCityDiscounts.length > 0 ? allCityDiscounts.reduce((a, b) => a + b, 0) / allCityDiscounts.length : 0
            } else {
                const allCompetitors = skus.flatMap(sku => sku.competitors ?? [])
                const prices = allCompetitors.filter(c => c.price != null && c.price > 0).map(c => c.price as number)
                const discounts = allCompetitors.filter(c => c.discountPercent != null).map(c => c.discountPercent as number)
                avgSellingPrice = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0
                avgDiscountPercent = discounts.length > 0 ? discounts.reduce((a, b) => a + b, 0) / discounts.length : 0
            }

            return {
                categoryId: categoryName.toLowerCase().replace(/\s+/g, '-'),
                categoryName,
                competitorSkusMonitored: totalCompetitors,
                pricing: { avgSellingPrice },
                promotions: { avgDiscountPercent },
            }
        })
    }, [trackedSKUs, locationSelection, skusWithCityPrices])

    const categoryOptions = useMemo(() => [
        { value: 'all', label: 'All Categories' },
        ...categoryMetrics.map(c => ({ value: c.categoryId, label: c.categoryName }))
    ], [categoryMetrics])

    // Push filters into the primary TopBar (compact horizontal pills)
    useEffect(() => {
        setFilterContent(
            <div className="flex items-center gap-2">
                <TopBarFilterDropdown
                    value={platformFilter}
                    options={PLATFORM_OPTIONS}
                    onChange={(v) => setPlatformFilter(v as Platform | 'all')}
                />
                <div className="w-px h-4 bg-border/60" />
                <TopBarFilterDropdown
                    value={categoryFilter}
                    options={categoryOptions}
                    onChange={setCategoryFilter}
                />
                <div className="w-px h-4 bg-border/60" />
                <MultiSelectCityFilter
                    selection={locationSelection}
                    citiesWithClusters={citiesWithClusters}
                    onChange={setLocationSelection}
                    loading={citiesLoading || cityPricesLoading}
                />
                <div className="w-px h-4 bg-border/60" />
                <DateRangePicker
                    value={dateRange}
                    onChange={setDateRange}
                />
            </div>
        )
        return () => setFilterContent(null)
    }, [platformFilter, categoryFilter, locationSelection, dateRange, categoryOptions, citiesWithClusters, citiesLoading, cityPricesLoading, setFilterContent])

    const filteredCategories = useMemo(() => {
        if (categoryFilter === 'all') return categoryMetrics
        return categoryMetrics.filter(c => c.categoryId === categoryFilter)
    }, [categoryFilter, categoryMetrics])



    const convertTrackedSKUToCardFormat = useCallback((sku: TrackedSKUWithCompetitors, citySkuData?: TrackedSKUWithCityPrices): ProductCompetitivePosition => {
        const competitorCount = sku.competitors?.length ?? 0
        const competitorPresence = competitorCount > 5 ? 'High' : competitorCount > 2 ? 'Medium' : 'Low'
        const hasSingleCityPrice = citySkuData?.cityPrices?.length === 1
        const myPrice = hasSingleCityPrice ? (citySkuData.cityPrices[0].price ?? sku.price ?? 0) : (sku.price ?? 0)

        let avgCompetitorPrice = 0
        if (hasSingleCityPrice && citySkuData?.competitors) {
            const cityCompPrices = citySkuData.competitors.filter(c => c.cityPrices?.[0]?.price != null).map(c => c.cityPrices[0].price as number)
            avgCompetitorPrice = cityCompPrices.length > 0 ? cityCompPrices.reduce((a, b) => a + b, 0) / cityCompPrices.length : 0
        } else {
            const competitorPrices = sku.competitors
                ?.filter(c => c.price != null && c.price > 0)
                .map(c => c.price as number) ?? []
            avgCompetitorPrice = competitorPrices.length > 0
                ? competitorPrices.reduce((a, b) => a + b, 0) / competitorPrices.length
                : 0
        }

        return {
            productId: sku.id,
            name: sku.name,
            variantSize: sku.brand || 'Unknown Brand',
            imageUrl: sku.imageUrl || '',
            category: sku.category?.trim() || 'Uncategorized',
            myPrice,
            competitorPrice: avgCompetitorPrice,
            competitorPresence,
            visibilityAlerts: [],
        }
    }, [])

    const productsForSelectedCategory = useMemo(() => {
        let filtered = trackedSKUs
        const getSkuCategory = (sku: TrackedSKUWithCompetitors) => (sku.category?.trim() || 'Uncategorized').toLowerCase()

        if (platformFilter !== 'all') filtered = filtered.filter(sku => sku.platform === platformFilter)

        if (selectedCategory) {
            const category = categoryMetrics.find(c => c.categoryId === selectedCategory)
            if (category) filtered = filtered.filter(sku => getSkuCategory(sku) === category.categoryName.toLowerCase())
        } else if (categoryFilter !== 'all') {
            const category = categoryMetrics.find(c => c.categoryId === categoryFilter)
            if (category) filtered = filtered.filter(sku => getSkuCategory(sku) === category.categoryName.toLowerCase())
        }

        return filtered.map(sku => convertTrackedSKUToCardFormat(sku, skusWithCityPrices.find(s => s.id === sku.id)))
    }, [trackedSKUs, platformFilter, selectedCategory, categoryFilter, categoryMetrics, convertTrackedSKUToCardFormat, skusWithCityPrices])

    const convertCityPricesToLocationAvailability = useCallback((cityPrices: CityPriceInfo[]): DummyCityAvailability[] => {
        const pincodeRegex = /^[0-9]{6}$/
        // Implementation kept identical to original to preserve exact logic.
        const cityMap = new Map<string, CityPriceInfo[]>();
        for (const cp of cityPrices) {
            const normalizedName = cp.cityName.trim().toUpperCase();
            const existing = cityMap.get(normalizedName) || [];
            existing.push(cp);
            cityMap.set(normalizedName, existing);
        }
        const deduplicatedCities: CityPriceInfo[] = [];
        for (const [, entries] of cityMap) {
            if (entries.length === 1) deduplicatedCities.push(entries[0]);
            else {
                deduplicatedCities.push({
                    ...entries[0],
                    clusterPrices: entries.flatMap(e => e.clusterPrices || []).filter((cluster, index, self) => index === self.findIndex(c => c.clusterId === cluster.clusterId)),
                    storePrices: entries.flatMap(e => e.storePrices || []).filter((store, index, self) => index === self.findIndex(s => s.storeId === store.storeId)),
                });
            }
        }
        return deduplicatedCities.map(cp => {
            const buildPincodesFromStorePrices = (stores: NonNullable<CityPriceInfo['storePrices']>) => {
                const pincodeMap = new Map<string, typeof stores>();
                stores.forEach(sp => {
                    const pincode = sp.pincode?.trim()
                    // Group stores without a valid 6-digit pincode under "Unknown"
                    const key = (pincode && pincodeRegex.test(pincode)) ? pincode : 'Unknown'
                    const existing = pincodeMap.get(key) || []
                    pincodeMap.set(key, [...existing, sp])
                });
                return Array.from(pincodeMap.entries()).map(([pincode, pincodeStores]) => {
                    const inStockStores = pincodeStores.filter(s => s.inStock);
                    const pinAvgMrp = pincodeStores.reduce((sum, s) => sum + (s.mrp ?? 0), 0) / pincodeStores.length;
                    const pinAvgPrice = pincodeStores.reduce((sum, s) => sum + (s.price ?? 0), 0) / pincodeStores.length;
                    const pinDiscount = pinAvgMrp > 0 ? Math.round(((pinAvgMrp - pinAvgPrice) / pinAvgMrp) * 10000) / 100 : 0;
                    return {
                        pincode, stockStatus: inStockStores.length > 0 ? 'In Stock' as const : 'Out of Stock' as const, mrp: pinAvgMrp || null, sellingPrice: pinAvgPrice, discountPercent: pinDiscount, ratings: pincodeStores.reduce((sum, s) => sum + (s.rating ?? 0), 0) / pincodeStores.length, darkstoreCount: pincodeStores.length, darkstoresStocked: inStockStores.length,
                        darkstores: pincodeStores.map(sp => ({ darkstoreId: `store-${sp.storeId}`, darkstoreName: sp.storeName, stockStatus: sp.inStock ? 'In Stock' as const : 'Out of Stock' as const, mrp: sp.mrp ?? null, sellingPrice: sp.price ?? 0, discountPercent: sp.discountPercent ?? 0, ratings: sp.rating ?? 0, merchantType: sp.merchantType || null })),
                    };
                })
            }

            let clusters = cp.clusterPrices && cp.clusterPrices.length > 0
                ? cp.clusterPrices.map(clp => {
                    // Accept both valid 6-digit pincodes and the synthetic "Unknown" key
                    const pincodes = clp.pincodePrices && clp.pincodePrices.length > 0
                        ? clp.pincodePrices
                            .filter(pp => pincodeRegex.test(pp.pincode) || pp.pincode === 'Unknown')
                            .map(pp => ({
                                pincode: pp.pincode, stockStatus: pp.inStock ? 'In Stock' as const : 'Out of Stock' as const, mrp: pp.mrp ?? null, sellingPrice: pp.price ?? 0, discountPercent: pp.discountPercent ?? 0, ratings: pp.rating ?? 0, darkstoreCount: pp.storeCount, darkstoresStocked: pp.storesInStock,
                                darkstores: pp.storePrices.map(sp => ({
                                    darkstoreId: `store-${sp.storeId}`, darkstoreName: sp.storeName, stockStatus: sp.inStock ? 'In Stock' as const : 'Out of Stock' as const, mrp: sp.mrp ?? null, sellingPrice: sp.price ?? 0, discountPercent: sp.discountPercent ?? 0, ratings: sp.rating ?? 0, merchantType: sp.merchantType || null,
                                })),
                            }))
                        : [];
                    return {
                        clusterId: `cluster-${clp.clusterId}`, clusterName: clp.clusterName, stockStatus: clp.inStock ? 'In Stock' as const : 'Out of Stock' as const, approxInventory: 0, mrp: clp.mrp ?? null, sellingPrice: clp.price ?? 0, discountPercent: clp.discountPercent ?? 0, ratings: clp.rating ?? 0, merchantType: null, rank: 0, pincodeCount: pincodes.length, pincodesStocked: pincodes.filter(p => p.stockStatus === 'In Stock').length, pincodes,
                    };
                // Keep clusters even if they only have "Unknown" pincodes
                }).filter(cluster => cluster.pincodes.length > 0)
                : []

            if (clusters.length === 0 && cp.storePrices && cp.storePrices.length > 0) {
                // Use all stores, grouping invalid-pincode stores under "Unknown"
                const pincodes = buildPincodesFromStorePrices(cp.storePrices)
                if (pincodes.length > 0) {
                    const allStores = cp.storePrices
                    const inStockStores = allStores.filter(s => s.inStock)
                    const clAvgMrp = allStores.reduce((sum, s) => sum + (s.mrp ?? 0), 0) / allStores.length
                    const clAvgPrice = allStores.reduce((sum, s) => sum + (s.price ?? 0), 0) / allStores.length
                    const clDiscount = clAvgMrp > 0 ? Math.round(((clAvgMrp - clAvgPrice) / clAvgMrp) * 10000) / 100 : 0
                    clusters = [{
                        clusterId: `cluster-${cp.cityId}-all`, clusterName: 'All', stockStatus: inStockStores.length > 0 ? 'In Stock' as const : 'Out of Stock' as const, approxInventory: 0, mrp: clAvgMrp || null, sellingPrice: clAvgPrice, discountPercent: clDiscount, ratings: allStores.reduce((sum, s) => sum + (s.rating ?? 0), 0) / allStores.length, merchantType: null, rank: 0, pincodeCount: pincodes.length, pincodesStocked: pincodes.filter(p => p.stockStatus === 'In Stock').length, pincodes,
                    }]
                }
            }

            const clustersInStock = clusters.filter(c => c.stockStatus === 'In Stock').length;
            return {
                cityId: cp.cityId.toString(), cityName: cp.cityName, clustersCount: clusters.length, clustersStocked: clustersInStock, stockedPercentage: clusters.length > 0 ? Math.round((clustersInStock / clusters.length) * 100) : 0, avgSellingPrice: cp.price ?? 0, avgMrp: cp.mrp ?? null, avgDiscountPercent: cp.discountPercent ?? 0, avgRatings: cp.rating ?? 0, rank: 0, clusters,
            };
        })
    }, [])

    const competitorBrandsForSelectedProduct = useMemo<DummyCompetitorBrandGroup[]>(() => {
        if (!showCompetitorsFor) return []
        const skuWithPrices = skusWithCityPrices.find(sku => sku.id === showCompetitorsFor)
        const trackedSKU = trackedSKUs.find(sku => sku.id === showCompetitorsFor)
        const competitors = skuWithPrices?.competitors || trackedSKU?.competitors || []
        if (competitors.length === 0) return []

        const brandMap = new Map<string, any[]>()
        competitors.forEach(comp => {
            const brand = comp.brand || 'Other Brands'
            const existing = brandMap.get(brand) || []
            brandMap.set(brand, [...existing, comp])
        })

        return Array.from(brandMap.entries()).map(([brand, brandCompetitors]) => {
            const prices = brandCompetitors.filter(c => c.price != null).map(c => c.price as number)
            const discounts = brandCompetitors.filter(c => c.discountPercent != null).map(c => c.discountPercent as number)
            const ratings = brandCompetitors.filter(c => c.rating != null).map(c => c.rating as number)

            return {
                brand, skuCount: brandCompetitors.length,
                avgRating: ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0,
                avgDiscount: discounts.length > 0 ? discounts.reduce((a, b) => a + b, 0) / discounts.length : 0,
                avgSellingPrice: prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0,
                skus: brandCompetitors.map(comp => {
                    const quantityStr = 'quantity' in comp ? comp.quantity : null
                    const quantity = quantityStr ? parseFloat(quantityStr) : null
                    return {
                        id: comp.id, name: comp.name, imageUrl: ('imageUrl' in comp ? comp.imageUrl : null) || null,
                        variantSize: quantityStr ? `${quantityStr}${'unit' in comp ? comp.unit : ''}` : '1 Pack',
                        quantity: isNaN(quantity as number) ? null : quantity, unit: ('unit' in comp ? comp.unit : null) || null,
                        avgRating: comp.rating ?? 0, mrp: comp.mrp ?? 0, discountPercent: comp.discountPercent ?? 0, sellingPrice: comp.price ?? 0, avgRank: 0,
                        locationAvailability: convertCityPricesToLocationAvailability('cityPrices' in comp ? (comp.cityPrices || []) : []),
                    }
                }),
            }
        })
    }, [showCompetitorsFor, trackedSKUs, skusWithCityPrices, convertCityPricesToLocationAvailability])

    // Get user's location-level price data for comparison in sidebar
    const userLocationDataForSelectedProduct = useMemo<UserLocationData | null>(() => {
        if (!showCompetitorsFor) return null
        const skuWithPrices = skusWithCityPrices.find(sku => sku.id === showCompetitorsFor)
        if (!skuWithPrices) return null
        
        return {
            aggregate: {
                price: skuWithPrices.price,
                mrp: skuWithPrices.mrp,
                discountPercent: skuWithPrices.discountPercent,
                inStock: true  // Tracked SKUs are assumed active
            },
            cityPrices: skuWithPrices.cityPrices || []
        }
    }, [showCompetitorsFor, skusWithCityPrices])

    // UI Actions
    const handleCategorySelect = useCallback((category: CategoryMetrics) => {
        setSelectedCategory(category.categoryId === selectedCategory ? null : category.categoryId)
        setSelectedProduct(null)
        setShowCompetitorsFor(null)
    }, [selectedCategory])

    const handleProductClick = useCallback((product: ProductCompetitivePosition) => {
        setSelectedProduct(product)
        setShowCompetitorsFor(product.productId)
    }, [])

    const handleCloseSidebar = () => {
        setSelectedProduct(null)
        setShowCompetitorsFor(null)
    }

    // EMPTY STATE
    if (!settingsLoading && trackedSKUs.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[80vh] p-4 animate-in fade-in zoom-in-95 duration-500">
                <div className="bg-background/40 backdrop-blur-2xl border border-border/40 p-12 rounded-[2rem] shadow-2xl flex flex-col items-center max-w-lg text-center">
                    <div className="h-24 w-24 bg-primary/10 rounded-full flex items-center justify-center mb-8 shadow-inner">
                        <Package className="h-12 w-12 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold text-foreground tracking-tight mb-4">No Data Available</h2>
                    <p className="text-lg text-muted-foreground mb-10 leading-relaxed">
                        Your competition intelligence dashboard requires product SKUs. Add them in settings to unlock deep market insights.
                    </p>
                    <Button onClick={() => navigate('/app/settings')} size="lg" className="h-14 px-8 rounded-full text-base font-bold shadow-lg shadow-primary/20 transition-shadow duration-150 hover:shadow-primary/40">
                        <Plus className="mr-2 h-6 w-6" /> Configure Tracked SKUs
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="min-h-screen bg-[#FDFDFD] dark:bg-[#0A0A0A] text-foreground font-sans relative overflow-x-hidden">
                {/* Background Gradients for depth */}
                <div className="absolute top-0 inset-x-0 h-96 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none z-0" />



                <div className="max-w-[1600px] mx-auto p-4 md:p-6 relative z-10 flex flex-col gap-6">

                    {/* ========================================== */}
                    {/* MARKET SEGMENTS - HORIZONTAL SCROLL         */}
                    {/* ========================================== */}
                    <section>
                        <div className="flex items-center gap-2 mb-4 px-1">
                            <h3 className="module-section-title">Categories Segment</h3>
                            {settingsLoading ? (
                                <span className="h-5 w-8 bg-muted animate-pulse rounded-md" />
                            ) : (
                                <span className="module-section-count">{filteredCategories.length + 1}</span>
                            )}
                        </div>

                        {settingsLoading ? (
                            <div className="flex gap-3 overflow-x-auto py-5 pr-6 scrollbar-none" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="flex-shrink-0 w-[300px]">
                                        <CategoryMetricsCardSkeleton />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="relative">
                                {/* Cards container — hidden scrollbar */}
                                <div
                                    ref={categoryScrollRef}
                                    className="flex gap-3 overflow-x-auto py-5 pr-6 snap-x snap-mandatory scrollbar-none"
                                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                                >
                                    {/* "All Categories" default card */}
                                    <div className="snap-start flex-shrink-0 w-[300px] ml-3 pl-5">
                                        <CategoryMetricsCard
                                            category={{
                                                categoryId: '__all__',
                                                categoryName: 'All Categories',
                                                competitorSkusMonitored: categoryMetrics.reduce((sum, c) => sum + c.competitorSkusMonitored, 0),
                                                pricing: {
                                                    avgSellingPrice: categoryMetrics.length > 0
                                                        ? categoryMetrics.reduce((sum, c) => sum + c.pricing.avgSellingPrice, 0) / categoryMetrics.length
                                                        : 0,
                                                },
                                                promotions: {
                                                    avgDiscountPercent: categoryMetrics.length > 0
                                                        ? categoryMetrics.reduce((sum, c) => sum + c.promotions.avgDiscountPercent, 0) / categoryMetrics.length
                                                        : 0,
                                                },
                                            }}
                                            isSelected={selectedCategory === null}
                                            isExpanded={false}
                                            onSelect={() => {
                                                setSelectedCategory(null)
                                                setSelectedProduct(null)
                                                setShowCompetitorsFor(null)
                                            }}
                                            onToggleExpand={() => { }}
                                        />
                                    </div>
                                    {filteredCategories.map((category) => (
                                        <div key={category.categoryId} className="snap-start flex-shrink-0 w-[300px]">
                                            <CategoryMetricsCard
                                                category={category}
                                                isSelected={selectedCategory === category.categoryId}
                                                isExpanded={false}
                                                onSelect={() => handleCategorySelect(category)}
                                                onToggleExpand={() => { }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>

                    {/* ========================================== */}
                    {/* PRODUCTS SECTION                             */}
                    {/* ========================================== */}
                    <section className="pb-20">
                        {/* Products Header */}
                        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
                            <div>
                                <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                                    <LayoutGrid className="w-6 h-6 text-primary" />
                                    {selectedCategory
                                        ? categoryMetrics.find(c => c.categoryId === selectedCategory)?.categoryName
                                        : categoryFilter !== 'all'
                                            ? categoryOptions.find(c => c.value === categoryFilter)?.label
                                            : 'All Products'
                                    }
                                </h2>
                                <p className="text-sm text-muted-foreground mt-1.5 font-medium">
                                    {settingsLoading
                                        ? 'Loading product catalog...'
                                        : `Showing ${productsForSelectedCategory.length} tracked SKUs`
                                    }
                                </p>
                            </div>


                        </div>

                        {/* Products Grid */}
                        {settingsLoading || cityPricesLoading ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <ProductCompetitiveCardSkeleton key={i} />
                                ))}
                            </div>
                        ) : productsForSelectedCategory.length === 0 ? (
                            <div className="flex items-center justify-center min-h-[400px] border border-dashed rounded-[2rem] bg-accent/20">
                                <p className="text-muted-foreground font-medium text-lg">No products match the selected criteria.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                                {productsForSelectedCategory.map((product) => (
                                    <ProductCompetitiveCard
                                        key={product.productId}
                                        product={product}
                                        isSelected={selectedProduct?.productId === product.productId}
                                        showCompetitors={false}
                                        onSelect={() => handleProductClick(product)}
                                        onToggleCompetitors={() => { }}
                                    />
                                ))}
                            </div>
                        )}
                    </section>
                </div>

            </div>

            {/* ========================================== */}
            {/* COMPETITOR RIGHT SIDEBAR (OVERLAY)         */}
            {/* ========================================== */}
            {selectedProduct && showCompetitorsFor && createPortal(
                <div className="fixed inset-0 z-50">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/80 animate-in fade-in duration-300"
                        onClick={handleCloseSidebar}
                    />

                    {/* Sidebar Panel - slides in from right */}
                    <div className="absolute right-0 top-0 h-full w-full lg:w-[75vw] max-w-6xl bg-background shadow-2xl border-l border-border/40 flex flex-col animate-in slide-in-from-right duration-400 ease-out sm:rounded-l-3xl overflow-hidden">

                        {/* Elegant Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 sm:px-10 py-6 sm:py-8 border-b border-border/40 bg-card/50 backdrop-blur-xl flex-shrink-0 z-10">
                            <div className="flex items-center gap-5 flex-1 min-w-0">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                                    {selectedProduct!.imageUrl ? (
                                        <img src={selectedProduct!.imageUrl} alt={selectedProduct!.name} className="relative w-20 h-20 sm:w-24 sm:h-24 object-contain bg-white rounded-2xl border border-border/50 p-2 shadow-lg flex-shrink-0" />
                                    ) : (
                                        <div className="relative w-20 h-20 sm:w-24 sm:h-24 bg-muted rounded-2xl border border-border flex items-center justify-center">
                                            <Package className="w-8 h-8 text-muted-foreground" />
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider rounded-md mb-2">
                                        {selectedProduct!.category}
                                    </span>
                                    <h3 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight truncate">
                                        {stripBrandPrefixFromProductName(selectedProduct!.name, selectedProduct!.variantSize)}
                                    </h3>
                                    <p className="text-base font-semibold text-muted-foreground mt-1 flex items-center gap-2">
                                        <span>{selectedProduct!.variantSize}</span>
                                        <span className="w-1.5 h-1.5 rounded-full bg-border" />
                                        <span>Market Analysis</span>
                                    </p>

                                    {/* Price */}
                                    {selectedProduct!.myPrice > 0 && (
                                        <div className="flex items-center gap-2 mt-3 px-3 py-1.5 rounded-xl bg-white/50 dark:bg-white/[0.06] border border-white/40 dark:border-white/[0.08] w-fit">
                                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Price</span>
                                            <span className="text-sm font-extrabold text-foreground leading-none">
                                                ₹{formatPrice(selectedProduct!.myPrice)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <Button
                                variant="secondary"
                                size="icon"
                                className="rounded-xl hover:bg-destructive hover:text-destructive-foreground transition-colors h-9 w-9 flex-shrink-0 shadow-sm"
                                onClick={handleCloseSidebar}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-4 sm:p-8 relative">
                            {cityPricesLoading && (
                                <div className="absolute inset-0 bg-background/30 backdrop-blur-[2px] z-30 flex items-center justify-center">
                                    <div className="flex items-center gap-3 bg-muted/80 border border-border/40 rounded-xl px-5 py-3 shadow-sm">
                                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                        <span className="text-sm text-muted-foreground font-medium">Refreshing data...</span>
                                    </div>
                                </div>
                            )}

                            <CompetitorSKUsTable
                                key={`${showCompetitorsFor}-${locationSelection.cityIds.join(',')}-${locationSelection.clusterIds.join(',')}-${platformFilter}`}
                                productName={selectedProduct!.name}
                                productBrand={selectedProduct!.variantSize}
                                competitorBrands={competitorBrandsForSelectedProduct}
                                userBrand={userBrand}
                                userLocationData={userLocationDataForSelectedProduct}
                            />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    )
}
